import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    VStack,
    HStack,
    Text,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    SimpleGrid,
    List,
    ListItem,
    ListIcon,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Badge,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleBreadcrumb } from "../../components/ModuleBreadcrumb";
import profileModuleConfig from "./moduleConfig";
import { brandConfig } from "../../brandConfig";
import { GET_CURRENT_TENANT, UPDATE_TENANT_TIER, GET_SUBSCRIPTION_PRODUCTS } from "./queries";

// Add-on Services
const ADDON_SERVICES = [
    {
        category: "Communication",
        items: [
            { name: "Additional Phone Numbers", price: "$10/month each", id: "phone_number" },
            { name: "International Calling", price: "$50/month", id: "intl_calling" },
            { name: "Premium Transcription", price: "$30/month", id: "transcription" },
            { name: "Video Recording Storage", price: "$20/month (100GB)", id: "video_storage" }
        ]
    },
    {
        category: "AI & Automation",
        items: [
            { name: "Custom AI Agent", price: "$500/month each", id: "ai_agent" },
            { name: "Advanced Analytics Dashboard", price: "$150/month", id: "analytics" },
            { name: "Sentiment Analysis", price: "$100/month", id: "sentiment" },
            { name: "Predictive Analytics", price: "$200/month", id: "predictive" }
        ]
    },
    {
        category: "Process & Compliance",
        items: [
            { name: "Process Mapping Session", price: "$500 one-time", id: "process_map" },
            { name: "Compliance Monitoring", price: "$300/month", id: "compliance" },
            { name: "Audit Trail System", price: "$150/month", id: "audit" },
            { name: "Document Management", price: "$100/month", id: "docs" }
        ]
    },
    {
        category: "Technical",
        items: [
            { name: "Additional Storage", price: "$10/month per 10GB", id: "storage" },
            { name: "Dedicated Server", price: "$200/month", id: "server" },
            { name: "Load Balancing", price: "$150/month", id: "load_balance" },
            { name: "DDoS Protection", price: "$100/month", id: "ddos" }
        ]
    },
    {
        category: "Support",
        items: [
            { name: "24/7 Support", price: "$300/month", id: "support_247" },
            { name: "Dedicated Slack Channel", price: "$150/month", id: "slack" },
            { name: "Weekly Check-ins", price: "$200/month", id: "checkins" },
            { name: "Emergency Response SLA", price: "$500/month", id: "sla" }
        ]
    }
];

const SubscriptionOffers = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [selectingPlan, setSelectingPlan] = useState<string | null>(null);

    const bg = brandConfig.colors.background.main;
    const cardBg = brandConfig.colors.background.cardGradient;
    const borderColor = brandConfig.colors.border.darkCard;
    const textPrimary = brandConfig.colors.text.inverse;
    const textSecondary = brandConfig.colors.text.secondaryDark;
    const textMuted = brandConfig.colors.text.mutedDark;
    const currentTenantId = brandConfig.tenantId || "684d0930cc7a27bb01ac83ce";

    // Fetch current tenant to show current plan
    const { data: tenantData, loading: tenantLoading, refetch } = useQuery(GET_CURRENT_TENANT, {
        variables: { id: currentTenantId },
    });

    // Fetch subscription products from database
    const { data: productsData, loading: productsLoading } = useQuery(GET_SUBSCRIPTION_PRODUCTS, {
        variables: { tenantId: currentTenantId },
    });
    const subscriptionPlans = productsData?.publicSubscriptionProducts || [];

    const [updateTier] = useMutation(UPDATE_TENANT_TIER, {
        onCompleted: (data) => {
            const tier = data.updateTenant.subscriptionTier;
            toast({
                title: "Plan Updated!",
                description: `Your plan has been set to ${tier}. You'll be billed on the 1st of each month.`,
                status: "success",
                duration: 6000,
                isClosable: true,
            });
            setSelectingPlan(null);
            refetch();

            // Encourage card on file if none exists
            if (!tenantData?.tenant?.stripeCustomerId) {
                setTimeout(() => {
                    toast({
                        title: "Save a Payment Method",
                        description: "Add a card on file to make bill payments easier.",
                        status: "info",
                        duration: 8000,
                        isClosable: true,
                    });
                }, 2000);
            }
        },
        onError: (err) => {
            toast({
                title: "Failed to update plan",
                description: err.message,
                status: "error",
                duration: 5000,
            });
            setSelectingPlan(null);
        },
    });

    const currentTier = tenantData?.tenant?.subscriptionTier;

    const handleSelectPlan = (plan: any) => {
        if (plan.tier === currentTier) return;
        setSelectingPlan(plan.id);
        updateTier({
            variables: {
                id: currentTenantId,
                input: { subscriptionTier: plan.tier },
            },
        });
    };

    if (tenantLoading || productsLoading) {
        return (
            <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
                <NavbarWithCallToAction />
                <ModuleBreadcrumb moduleConfig={profileModuleConfig} />
                <Container maxW="container.lg" py={12} flex="1">
                    <VStack spacing={4}>
                        <Spinner size="xl" />
                        <Text color={textSecondary}>Loading plans...</Text>
                    </VStack>
                </Container>
                <FooterWithFourColumns />
            </Box>
        );
    }

    return (
        <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <ModuleBreadcrumb moduleConfig={profileModuleConfig} />

            <Container maxW="container.xl" py={12} flex="1">
                <VStack spacing={8} align="stretch">
                    {/* Header */}
                    <VStack spacing={3} textAlign="center">
                        <HStack spacing={3} justify="center">
                            <Heading size="2xl" color={textPrimary}>
                                Progressive Business Growth Plans
                            </Heading>
                            <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">CUSTOMER</Badge>
                        </HStack>
                        <Text fontSize="lg" color={textSecondary}>
                            Start with Foundation and scale as you grow. Each tier builds on the previous.
                        </Text>
                        <Text fontSize="md" color={textMuted}>
                            All plans are month-to-month. Billed on the 1st — pay with card or reward tokens.
                        </Text>
                    </VStack>

                    {/* Current Plan Alert */}
                    {currentTier && (
                        <Alert status="info" bg={cardBg} borderColor={borderColor}>
                            <AlertIcon />
                            <Box>
                                <Text fontWeight="bold" color={textPrimary}>
                                    Current plan: {currentTier}
                                </Text>
                                <Text fontSize="sm" color={textSecondary}>
                                    Manage your payment methods in your{" "}
                                    <Button
                                        as="a"
                                        href="/profile/payment-methods"
                                        variant="link"
                                        size="sm"
                                        color={brandConfig.colors.primary}
                                    >
                                        payment settings
                                    </Button>
                                </Text>
                            </Box>
                        </Alert>
                    )}

                    {/* Subscription Plans */}
                    {subscriptionPlans.length === 0 ? (
                        <Alert status="warning" bg={cardBg} borderColor={borderColor}>
                            <AlertIcon />
                            <Text color={textPrimary}>No subscription plans available.</Text>
                        </Alert>
                    ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                            {subscriptionPlans.map((plan: any) => {
                                const isCurrentPlan = plan.tier === currentTier;
                                const planColor = plan.color || "blue";
                                const displayPrice = `$${plan.amount}`;
                                const displayInterval = plan.interval === "month" ? "per month" : plan.interval === "year" ? "per year" : `per ${plan.interval}`;

                                return (
                                    <Card
                                        key={plan.id}
                                        position="relative"
                                        overflow="hidden"
                                        borderWidth={isCurrentPlan ? "2px" : plan.isRecommended ? "2px" : "1px"}
                                        borderColor={isCurrentPlan ? "green.400" : plan.isRecommended ? brandConfig.colors.primary : borderColor}
                                        bg={cardBg}
                                        backdropFilter="blur(10px)"
                                        boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                                        _hover={{
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        {/* Badge */}
                                        {(isCurrentPlan || plan.badge) && (
                                            <Box
                                                position="absolute"
                                                top="0"
                                                right="0"
                                                bg={isCurrentPlan ? "green.500" : plan.isRecommended ? brandConfig.colors.primary : `${planColor}.500`}
                                                color="white"
                                                px={3}
                                                py={1}
                                                fontSize="xs"
                                                fontWeight="bold"
                                                borderBottomLeftRadius="md"
                                            >
                                                {isCurrentPlan ? "Current Plan" : plan.badge}
                                            </Box>
                                        )}

                                        <CardHeader pb={3}>
                                            <VStack align="start" spacing={2}>
                                                <Heading size="md" color={textPrimary}>
                                                    {plan.name}
                                                </Heading>
                                                <HStack>
                                                    <Text fontSize="2xl" fontWeight="bold" color={textPrimary}>
                                                        {displayPrice}
                                                    </Text>
                                                    <Text fontSize="sm" color={textMuted}>
                                                        {displayInterval}
                                                    </Text>
                                                </HStack>
                                                <Text fontSize="xs" color={textSecondary}>
                                                    {plan.description}
                                                </Text>
                                            </VStack>
                                        </CardHeader>

                                        <CardBody py={4}>
                                            <List spacing={2}>
                                                {plan.features.slice(0, 6).map((feature: string, idx: number) => (
                                                    <ListItem key={idx} fontSize="xs">
                                                        <HStack align="start" spacing={1}>
                                                            <ListIcon
                                                                as={CheckIcon}
                                                                color={`${planColor}.500`}
                                                                mt={0.5}
                                                                minW="16px"
                                                            />
                                                            <Text color={textPrimary}>
                                                                {feature}
                                                            </Text>
                                                        </HStack>
                                                    </ListItem>
                                                ))}
                                                {plan.features.length > 6 && (
                                                    <Text fontSize="xs" color={textMuted} pl={6}>
                                                        +{plan.features.length - 6} more features
                                                    </Text>
                                                )}
                                            </List>
                                        </CardBody>

                                        <CardFooter>
                                            {isCurrentPlan ? (
                                                <Button
                                                    w="100%"
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor="green.400"
                                                    color="green.400"
                                                    isDisabled
                                                >
                                                    Current Plan
                                                </Button>
                                            ) : (
                                                <Button
                                                    w="100%"
                                                    size="sm"
                                                    bg={plan.isRecommended ? brandConfig.colors.primary : "transparent"}
                                                    color={plan.isRecommended ? "white" : textPrimary}
                                                    borderWidth={plan.isRecommended ? "0" : "1px"}
                                                    borderColor={borderColor}
                                                    _hover={{
                                                        bg: plan.isRecommended ? brandConfig.colors.primaryHover : brandConfig.colors.background.main,
                                                        transform: "scale(1.02)"
                                                    }}
                                                    isLoading={selectingPlan === plan.id}
                                                    onClick={() => handleSelectPlan(plan)}
                                                >
                                                    {plan.ctaText || "Select Plan"}
                                                </Button>
                                            )}
                                        </CardFooter>
                                    </Card>
                                );
                            })}
                        </SimpleGrid>
                    )}

                    {/* Add-on Services Section */}
                    <Box mt={12}>
                        <VStack spacing={6} align="stretch">
                            <VStack spacing={2} textAlign="center">
                                <Heading size="xl" color={textPrimary}>
                                    Power-Up with Add-Ons
                                </Heading>
                                <Text fontSize="md" color={textSecondary}>
                                    Enhance any plan with these additional services
                                </Text>
                            </VStack>

                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                                {ADDON_SERVICES.map((category) => (
                                    <Card
                                        key={category.category}
                                        bg={cardBg}
                                        borderColor={borderColor}
                                        borderWidth="1px"
                                    >
                                        <CardHeader>
                                            <Heading size="sm" color={textPrimary}>
                                                {category.category}
                                            </Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <List spacing={3}>
                                                {category.items.map((item) => (
                                                    <ListItem key={item.id}>
                                                        <VStack align="start" spacing={0}>
                                                            <Text fontSize="sm" color={textPrimary}>
                                                                {item.name}
                                                            </Text>
                                                            <Text fontSize="xs" color={brandConfig.colors.primary} fontWeight="bold">
                                                                {item.price}
                                                            </Text>
                                                        </VStack>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </CardBody>
                                    </Card>
                                ))}
                            </SimpleGrid>
                        </VStack>
                    </Box>
                </VStack>
            </Container>

            <FooterWithFourColumns />
        </Box>
    );
};

export default SubscriptionOffers;
